import {
  createUserSession,
  UserSessionAttributes,
} from "../models/user-session-model";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getIpAddressFunc } from "../components/common/utils/ip-address";
import { loginUser, validateUserToken } from "../store/actions/user-actions";
import { storeUserSession } from './helpers/session-helpers';
import { LinearTransition } from "react-native-reanimated";
import { generatePassword } from "../components/common/utils/password-gen";
import { 
  isSuccessful, 
  ApiRequest, 
  ApiResponse, 
  fetchDataFromAPITyped 
} from "./api-requests";

export const getUserSessionObj = async (): Promise<UserSessionAttributes> => {
  // const token =  await AsyncStorage.getItem("token");
  // const IpAddress = await AsyncStorage.getItem("IpAddress");
  // const email = await AsyncStorage.getItem("email");
  // const UserId = await AsyncStorage.getItem("userId");
  // const role = await AsyncStorage.getItem("role");
  // Retrieve the full token object from AsyncStorage
  const tokenData = await AsyncStorage.getItem("userSession");

  let createSession;
  let sessionObj;
  if (tokenData) {
    // Parse the JSON stored in token
    const parsedToken: Partial<UserSessionAttributes> = JSON.parse(tokenData);
    // Check if all necessary fields exist in the parsed token
    const requiredFields = ["access_token", "IpAddress", "email", "id", "role"];
    const isTokenValid = requiredFields.every(
      (field) => parsedToken[field as keyof UserSessionAttributes] !== undefined
    );
    sessionObj = createUserSession({
      access_token: parsedToken.access_token,
      IpAddress: parsedToken.IpAddress,
      email: parsedToken.email,
      username: parsedToken.username,
      id: parsedToken.id,
      role: Array.isArray(parsedToken.role)
        ? parsedToken.role
        : parsedToken.role
        ? [parsedToken.role as unknown as string]
        : ['guest'],
      primary_use: parsedToken.primary_use,
      first_name: parsedToken.first_name,
      last_name: parsedToken.last_name,
      service: parsedToken.service,
    });
    //store.dispatch(setSession(createSession))
  } else {
    //not logged in, set the guest login
    let ip = await getIpAddressFunc();
    if (!ip) {
      ip = "1.1.1.1";
    }
    createSession = createUserSession({
      email: "guest",
      username: "guest",
      password: generatePassword(16),
      IpAddress: ip,
      logintype: "guest",
      access_token: "",
      role: ["guest"],
      primary_use: "guest",
    });

    //get the token
    //store.dispatch(userActions.loginUser({userLogin: createSession}));
    //store.dispatch(setSession(sessionObj))
    const loginResult = await loginUser(createSession);
    if (loginResult) {
      sessionObj = loginResult as UserSessionAttributes;
    } else {
      sessionObj = createSession;
    }
  }

  return sessionObj;
};


/**
 * Retrieves the user session object from AsyncStorage and recreates it as a UserSessionAttributes object
 * @returns UserSessionAttributes | null - Returns the user session or null if not found
 */
export const getUserSession =
  async (): Promise<UserSessionAttributes | null> => {
    try {
      const sessionData = await AsyncStorage.getItem("userSession");
      if (sessionData !== null && sessionData !== "undefined") {
        // Parse the retrieved data and recreate the UserSessionAttributes object
        let parsedSession: UserSessionAttributes = JSON.parse(sessionData);
        if (
          parsedSession.message === "Invalid token" ||
          parsedSession.message === "Session not found"
        ) {
          //invalid session, we need to create a new one
          await AsyncStorage.clear();
          const ip = await getIpAddressFunc();
          const createSession = createUserSession({
            email: "guest",
            username: "guest",
            password: generatePassword(16),
            IpAddress: ip,
            logintype: "guest",
            access_token: "",
          });
          const loginResult = await loginUser(createSession);
          parsedSession = loginResult as UserSessionAttributes || createSession;
        }
        return createUserSession(parsedSession); // Use createUserSession to handle missing fields
      } else {
        //not logged in, set the guest login
        var ip = await getIpAddressFunc();
        if (!ip) {
          ip = "1.1.1.1";
        }
        const createSession = createUserSession({
          email: "guest",
          username: "guest",
          password: generatePassword(16),
          IpAddress: ip,
          logintype: "guest",
          access_token: "",
        });
        const loginResult = await loginUser(createSession);
        const sessionObj = loginResult as UserSessionAttributes || createSession;
        const validationResponse = await validateUserToken();
        if (isSuccessful(validationResponse.status) && validationResponse.payload) {
          return createUserSession(validationResponse.payload);
        }
        return sessionObj;
      }
    } catch (error) {
      console.error("Error retrieving user session: ", error);
      return null;
    }
  };

/**
 * Response type for auth token generation
 */
export type AuthTokenResponse = {
  token: string;
  expiresAt: string;
  message?: string;
};

/**
 * Response type for auth token validation
 */
export type ValidateAuthTokenResponse = {
  valid: boolean;
  session?: UserSessionAttributes;
  message?: string;
};

/**
 * Generates a single-use authentication token for secure mobile-to-web transitions.
 * This token can be used to authenticate a user on the web without exposing credentials.
 * 
 * @returns Promise<ApiResponse<AuthTokenResponse | null>> - Contains the auth token and expiration
 * 
 * @example
 * ```typescript
 * const response = await generateAuthToken();
 * if (isSuccessful(response.status) && response.payload?.token) {
 *   const webUrl = `${API_SERVER}/onboardingstatus?authToken=${response.payload.token}`;
 *   await Linking.openURL(webUrl);
 * }
 * ```
 */
export const generateAuthToken = async (): Promise<
  ApiResponse<AuthTokenResponse | null>
> => {
  const request: ApiRequest = {
    route: 'auth/generate-token',
    request: {
      method: 'POST',
      entity: 'auth',
      body: {}, // User ID will be extracted from JWT token in headers
    },
  };

  try {
    const response = await fetchDataFromAPITyped<AuthTokenResponse>(request);
    
    if (!isSuccessful(response.status)) {
      console.error('Failed to generate auth token:', response.payload);
      return { status: response.status, payload: null };
    }

    return response;
  } catch (error) {
    console.error('Error generating auth token:', error);
    return { status: undefined, payload: null };
  }
};

/**
 * Validates an authentication token and retrieves the associated user session.
 * This is called on the web side when a user arrives with an authToken parameter.
 * 
 * @param token - The single-use authentication token to validate
 * @returns Promise<ApiResponse<ValidateAuthTokenResponse | null>> - Contains validation result and session
 * 
 * @example
 * ```typescript
 * const urlParams = new URLSearchParams(window.location.search);
 * const authToken = urlParams.get('authToken');
 * 
 * if (authToken) {
 *   const response = await validateAuthToken(authToken);
 *   if (isSuccessful(response.status) && response.payload?.valid) {
 *     // Store the session and log the user in
 *     await AsyncStorage.setItem('token', response.payload.session.access_token);
 *     await storeUserSession(response.payload.session);
 *   }
 * }
 * ```
 */
export const validateAuthToken = async (
  token: string
): Promise<ApiResponse<ValidateAuthTokenResponse | null>> => {
  const request: ApiRequest = {
    route: 'auth/validate-token',
    request: {
      method: 'POST',
      entity: 'auth',
      body: { token },
    },
  };

  try {
    const response = await fetchDataFromAPITyped<ValidateAuthTokenResponse>(request);
    
    if (!isSuccessful(response.status)) {
      console.error('Failed to validate auth token:', response.payload);
      return { status: response.status, payload: null };
    }

    return response;
  } catch (error) {
    console.error('Error validating auth token:', error);
    return { status: undefined, payload: null };
  }
};
